import { Check, ChevronDown, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/shadcn/button';
import { Progress } from '@/components/shadcn/progress.tsx';

type SectionKey = 'programs' | 'projects' | 'properties';

const SECTION_KEYS: SectionKey[] = ['programs', 'projects', 'properties'];

interface SidebarProps {
  steps: { key: string; title: string }[];
  currentStep: number;
  selectedAreas?: string[];
  onStepClick?: (index: number) => void;
}

function groupStepsBySection(steps: { key: string; title: string }[], contextCount: number) {
  const sectionSteps: { sectionKey: SectionKey; steps: { key: string; title: string }[]; startIndex: number }[] = [];
  let i = contextCount;
  for (const sectionKey of SECTION_KEYS) {
    const prefix = `${sectionKey}-`;
    const startIndex = i;
    const group: { key: string; title: string }[] = [];
    while (i < steps.length - 1 && steps[i].key.startsWith(prefix)) {
      group.push(steps[i]);
      i++;
    }
    if (group.length > 0) {
      sectionSteps.push({ sectionKey, steps: group, startIndex });
    }
  }
  return sectionSteps;
}

export default function FormSidebar({ steps, currentStep, selectedAreas = [], onStepClick }: SidebarProps) {
  const [contextExpanded, setContextExpanded] = useState(true);
  const [expandedSections, setExpandedSections] = useState<Record<SectionKey, boolean>>({
    programs: false,
    projects: false,
    properties: false,
  });

  const contextSteps = steps.slice(0, 4);
  const reviewStep = steps.length > 4 ? steps[steps.length - 1] : null;
  const sectionGroups = groupStepsBySection(steps, 4);

  const CONTEXT_STEP_COUNT = 4;

  useEffect(() => {
    if (currentStep < CONTEXT_STEP_COUNT) {
      setContextExpanded(true);
      setExpandedSections({ programs: false, projects: false, properties: false });
    } else if (currentStep < steps.length - 1) {
      setContextExpanded(false);
      const groups = groupStepsBySection(steps, CONTEXT_STEP_COUNT);
      const activeGroup = groups.find((g) => currentStep >= g.startIndex && currentStep < g.startIndex + g.steps.length);
      if (activeGroup) {
        setExpandedSections({
          programs: activeGroup.sectionKey === 'programs',
          projects: activeGroup.sectionKey === 'projects',
          properties: activeGroup.sectionKey === 'properties',
        });
      }
    }
  }, [currentStep, steps]);

  const totalSteps = steps.length;
  const progressPercentage = Math.round(((currentStep + 1) / totalSteps) * 100);

  const contextCompleted = Math.min(currentStep, contextSteps.length);

  const getSectionCompleted = (startIndex: number, count: number) => {
    const completed = Math.max(0, currentStep - startIndex);
    return Math.min(completed, count);
  };

  const toggleSection = (key: SectionKey) => {
    setContextExpanded(false);
    setExpandedSections((prev) => {
      const isCurrentlyExpanded = prev[key];
      if (isCurrentlyExpanded) {
        return { ...prev, [key]: false };
      }
      return { programs: false, projects: false, properties: false, [key]: true };
    });
  };

  const toggleContext = () => {
    const next = !contextExpanded;
    setContextExpanded(next);
    if (next) {
      setExpandedSections({ programs: false, projects: false, properties: false });
    }
  };

  const isStepCompleted = (index: number) => index < currentStep;
  const isStepActive = (index: number) => index === currentStep;

  const renderStepItem = (step: { key: string; title: string }, index: number, offset = 0) => {
    const globalIndex = index + offset;
    const completed = isStepCompleted(globalIndex);
    const active = isStepActive(globalIndex);

    const content = (
      <>
        {completed ? (
          <Check className='w-4 h-4 text-green-600 shrink-0' />
        ) : active ? (
          <div className='w-3 h-3 rounded-full bg-green-600 shrink-0' />
        ) : (
          <div className='w-3 h-3 rounded-full border-2 border-gray-300 shrink-0' />
        )}
        <span className={completed || active ? 'text-gray-900' : 'text-gray-500'}>{step.title}</span>
      </>
    );

    const baseClass = `flex items-center gap-3 px-3 py-2 rounded-lg text-sm w-full text-left ${
      active ? 'bg-gray-100 font-bold' : 'hover:bg-gray-50'
    }`;

    if (onStepClick) {
      return (
        <button key={step.key} type='button' onClick={() => onStepClick(globalIndex)} className={baseClass}>
          {content}
        </button>
      );
    }

    return (
      <div key={step.key} className={baseClass}>
        {content}
      </div>
    );
  };

  return (
    <aside className='hidden lg:flex w-[300px] min-w-[300px] flex-col border-r border-gray-200 bg-white p-6'>
      {/* Progress header: Section X of Y (left) + % (right) on small; Overall Progress (left) + % (right) on md-xl */}
      <div className='mb-6'>
        <div className='flex justify-between items-center mb-2'>
          <span className='text-sm font-semibold text-gray-900 md:hidden'>
            Section {currentStep + 1} of {totalSteps}
          </span>
          <span className='text-sm font-semibold text-gray-900 hidden md:inline'>Overall Progress</span>
          <span className='text-sm text-gray-500'>{progressPercentage}%</span>
        </div>
        <Progress value={progressPercentage} className='h-2 text-black' />
      </div>

      <div className='h-px bg-gray-200 mb-4' />

      {/* Discovery Focus - hidden on small screens, visible md to xl */}
      <div className='mb-4 hidden md:block'>
        <span className='text-xs font-medium text-gray-500 uppercase tracking-wider'>Discovery Focus</span>
        <div className='mt-2 flex flex-wrap gap-2'>
          {selectedAreas.length > 0 ? (
            selectedAreas.map((area) => (
              <span
                key={area}
                className='inline-block px-3 py-1 text-sm font-medium text-gray-800 bg-gray-100 border border-gray-300 rounded-md capitalize'
              >
                {area}
              </span>
            ))
          ) : (
            <span className='text-sm text-gray-400 italic'>None selected</span>
          )}
        </div>
      </div>

      <div className='h-px bg-gray-200 mb-4' />

      {/* Navigation */}
      <div>
        <div className='flex-1'>
          <span className='text-xs font-medium text-gray-500 uppercase tracking-wider'>Navigation</span>

          <nav className='mt-4 space-y-1'>
            {/* Context Group */}
            <Button
              type='button'
              onClick={toggleContext}
              className={`w-full flex items-center justify-between bg-muted py-2 rounded-lg transition-colors ${
                currentStep < CONTEXT_STEP_COUNT ? 'bg-green-50 text-green-700 font-semibold ring-1 ring-green-200' : 'hover:bg-gray-50'
              }`}
            >
              <div className='flex items-center gap-2'>
                {contextExpanded ? <ChevronDown className='w-4 h-4 text-gray-500' /> : <ChevronRight className='w-4 h-4 text-gray-500' />}
                <span
                  className={`text-sm whitespace-nowrap ${currentStep < CONTEXT_STEP_COUNT ? 'font-bold text-green-700' : 'font-semibold text-green-600'}`}
                >
                  Context
                </span>
              </div>
              <span className='text-xs text-gray-500'>
                {contextCompleted} / {contextSteps.length}
              </span>
            </Button>

            {contextExpanded && <div className='ml-6 space-y-1'>{contextSteps.map((step, index) => renderStepItem(step, index, 0))}</div>}

            {/* Dynamic section groups (Programs, Projects, Properties) */}
            {sectionGroups.map(({ sectionKey, steps: sectionSteps, startIndex }) => {
              const expanded = expandedSections[sectionKey];
              const completed = getSectionCompleted(startIndex, sectionSteps.length);
              const isSectionActive = currentStep >= startIndex && currentStep < startIndex + sectionSteps.length;
              const sectionLabel = sectionKey.charAt(0).toUpperCase() + sectionKey.slice(1);
              return (
                <div key={sectionKey}>
                  <button
                    type='button'
                    onClick={() => toggleSection(sectionKey)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                      isSectionActive ? 'bg-green-50 text-green-700 font-semibold ring-1 ring-green-200' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className='flex items-center gap-2'>
                      {expanded ? <ChevronDown className='w-4 h-4 text-gray-500' /> : <ChevronRight className='w-4 h-4 text-gray-500' />}
                      <span className='text-sm font-medium'>{sectionLabel}</span>
                    </div>
                    <span className='text-xs text-gray-500'>
                      {completed} / {sectionSteps.length}
                    </span>
                  </button>
                  {expanded && (
                    <div className='ml-6 space-y-1'>{sectionSteps.map((step, index) => renderStepItem(step, index, startIndex))}</div>
                  )}
                </div>
              );
            })}

            {/* Review & Submit */}
            {reviewStep && <div className='pt-2'>{renderStepItem(reviewStep, steps.length - 1, 0)}</div>}
          </nav>
        </div>

        <div className='h-px bg-gray-200 my-4' />

        {/* Footer */}
        <div className='max-w-full'>
          <p className='text-xs text-gray-500 leading-relaxed'>
            Your responses will be combined with other departments to design a unified solution. All progress is automatically saved.
          </p>
        </div>
      </div>
    </aside>
  );
}
